import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth, type User } from '@/store/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Field, ErrorBox } from '@/components/common';
import { useBranding, BrandLogo } from '@/lib/branding';
import { t, hasStoredLanguage, setLanguage } from '@/i18n';
import { LanguageMenu } from '@/components/LanguageMenu';
import { InstallButton } from '@/lib/pwa';

export function homeFor(u: User | null): string {
  // agents and phone users with admin rights (a group such as Supervisor) start in the admin portal (2026-10-15)
  if (u?.admin_portal) return '/admin';
  return u?.role === 'AGENT' ? '/agent/dashboard' : u?.role === 'USER' ? '/me' : '/admin';
}

export default function Login() {
  const nav = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<unknown>(null);
  const [busy, setBusy] = React.useState(false);
  const brand = useBranding();
  // until this browser picks a language, the sign-in page is in the tenant's portal default
  React.useEffect(() => { if (!hasStoredLanguage()) setLanguage(brand.portal_default_language, false); }, [brand.portal_default_language]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api<{ token: string; user: User }>('/auth/login', { body: { username, password } });
      setSession(r.token, r.user);
      nav(homeFor(r.user), { replace: true });
    } catch (err) { setError(err); } finally { setBusy(false); }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <div className="absolute right-4 top-4 flex items-center gap-2"><InstallButton /><LanguageMenu /></div>
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <BrandLogo className="mb-2 h-10 max-w-[12rem]" />
          <CardTitle className="text-xl" data-testid="brand-name">{brand.product_name}</CardTitle>
          <CardDescription>{brand.login_message || t('Sign in to the admin or agent portal')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid gap-4">
            {/* on a tenant's own address the sign-in is for that tenant only */}
            <Field help="login.username" label={t('Username')} hint={brand.tenant ? undefined : t('Another company: username@company')}>
              <Input name="username" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus />
            </Field>
            <Field help="login.password" label={t('Password')}><Input name="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
            <ErrorBox error={error} />
            <Button type="submit" disabled={busy} className="w-full">{t('Sign in')}</Button>
            {brand.support_email && <p className="text-center text-xs text-muted-foreground">{t('Help:')} <a className="underline" href={`mailto:${brand.support_email}`}>{brand.support_email}</a></p>}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
